<template>
  <div class="production-plan-calendar ma-4">
    <!-- Page Title -->
    <div class="text-h5 font-weight-bold mb-1">
      Calendar
    </div>
    <div class="body-2 grey--text mb-5">
      ดูตารางวางจำหน่าย Collection และ Event
    </div>

    <!-- Brand Color Legend -->
    <div
      v-if="uniqueBrands.length"
      class="d-flex flex-wrap align-center mb-4"
      style="gap: 6px">
      <span class="caption grey--text mr-1">Brand:</span>
      <v-chip
        v-for="brand in uniqueBrands"
        :key="'legend-' + brand"
        x-small
        label
        :color="getBrandColor(brand).bg"
        :text-color="getBrandColor(brand).text"
      >
        {{ brand }}
      </v-chip>
    </div>

    <v-row>
      <!-- Calendar Section -->
      <v-col
        cols="12"
        md="7">
        <MonthCalendar
          v-model="selectedDay"
          :can-create-event="isControllerUser"
          @month-change="onMonthChange"
          @add-event="openAddEventDialog">
          <template #day-content="{ date }">
            <div
              v-if="getCollectionsForDate(date).length || getEventsForDate(date).length"
              class="d-flex flex-column mt-1"
              style="width:100%; flex:1; overflow:hidden;">
              <template v-for="(item) in getDayItems(date).slice(0, 3)">
                <!-- eslint-disable-next-line vue/valid-v-for -->
                <v-chip
                  x-small
                  label
                  :color="item.type === 'collection' ? getBrandColor(item.brand).bg : 'pink lighten-4'"
                  :text-color="item.type === 'collection' ? getBrandColor(item.brand).text : 'pink darken-2'"
                  class="mb-1"
                  style="font-size: 9px; height: 18px; max-width: 100%"
                  :title="item.name"
                >
                  <span
                    style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap;"
                  >{{ item.name }}</span>
                </v-chip>
              </template>
              <span
                v-if="getDayItems(date).length > 3"
                class="caption grey--text text-center"
                style="font-size: 9px"
              >
                +{{ getDayItems(date).length - 3 }}
              </span>
            </div>
          </template>
        </MonthCalendar>
      </v-col>

      <!-- Right Panel -->
      <v-col
        cols="12"
        md="5">
        <!-- Detail Panel -->
        <v-card
          rounded="lg"
          elevation="3"
          style="overflow: hidden">
          <v-toolbar
            color="secondary"
            dark
            flat
            dense>
            <v-icon
              small
              class="mr-2">
              mdi-calendar
            </v-icon>
            <span class="subtitle-2">รายละเอียดของวันที่ {{ selectedDay | dateUserFormatMonth }}</span>
          </v-toolbar>

          <div class="pa-4">
            <v-progress-linear
              v-if="loading"
              indeterminate
              color="secondary"
              class="mb-2"
            />

            <!-- Collections -->
            <div v-if="selectedDayCollections.length">
              <div class="caption grey--text mb-2">
                Collections
              </div>
              <v-card
                v-for="col in selectedDayCollections"
                :key="'col-' + col.id"
                outlined
                flat
                class="pa-3 mb-3"
                :style="`border-radius: 10px; border-left: 3px solid ${getBrandColor(col.brand).border}`"
              >
                <div class="d-flex align-center mb-1">
                  <div class="body-2 font-weight-bold flex-grow-1">
                    {{ col.name }}
                  </div>
                  <v-chip
                    v-if="col.brand"
                    x-small
                    label
                    :color="getBrandColor(col.brand).bg"
                    :text-color="getBrandColor(col.brand).text"
                  >
                    {{ col.brand }}
                  </v-chip>
                </div>
                <div class="caption grey--text mb-2">
                  Sales Date: {{ col.salesDate | dateUserFormatMonth }}
                </div>
                <div
                  v-if="getAssigneesByJob(col).length"
                  class="mb-2"
                >
                  <div
                    v-for="group in getAssigneesByJob(col)"
                    :key="group.job"
                    class="mb-3"
                  >
                    <div class="d-flex align-center mb-1">
                      <v-icon
                        small
                        color="grey darken-1"
                        class="mr-1">
                        mdi-account-outline
                      </v-icon>
                      <span class="caption grey--text text--darken-2">{{ group.label }}</span>
                    </div>
                    <div class="body-2 ml-6">
                      {{ group.names.join(', ') }}
                    </div>
                  </div>
                </div>
                <div
                  v-if="col.foreignMerchandiser"
                  class="mb-3">
                  <div class="d-flex align-center mb-1">
                    <v-icon
                      small
                      color="grey darken-1"
                      class="mr-1">
                      mdi-account-outline
                    </v-icon>
                    <span class="caption grey--text text--darken-2">Merchandiser (CN)</span>
                  </div>
                  <div class="body-2 ml-6">
                    {{ col.foreignMerchandiser }}
                  </div>
                </div>
                <div
                  v-if="col.prototypes && col.prototypes.length"
                  class="mt-1">
                  <div
                    v-for="proto in col.prototypes"
                    :key="proto.id"
                    class="d-flex align-center py-1"
                    style="border-bottom: 1px solid #f5f5f5">
                    <div class="flex-grow-1">
                      <div class="caption font-weight-medium">
                        {{ proto.sketch ? proto.sketch.sketchId : "" }}
                        {{ proto.name || "-" }}
                        {{ proto }}
                      </div>
                    </div>
                  </div>
                </div>
              </v-card>
            </div>

            <!-- Events -->
            <div v-if="selectedDayEvents.length">
              <div class="caption grey--text mb-2 mt-3">
                Events
              </div>
              <v-card
                v-for="(evt, i) in selectedDayEvents"
                :key="'evt-' + i"
                outlined
                flat
                class="pa-3 mb-2 d-flex align-center"
                style="border-radius: 10px; border-left: 3px solid #ec407a">
                <div class="flex-grow-1">
                  <div class="body-2 font-weight-bold">
                    {{ evt.name }}
                  </div>
                  <div
                    v-if="evt.description"
                    class="caption grey--text text--darken-1 mt-1">
                    {{ evt.description }}
                  </div>
                  <div class="caption grey--text mt-1">
                    {{ evt.startDate | dateUserFormatMonth }}
                    <template v-if="evt.endDate && !$dayjs(evt.endDate).isSame(evt.startDate, 'day')">
                      - {{ evt.endDate | dateUserFormatMonth }}
                    </template>
                  </div>
                </div>
                <v-btn
                  v-if="isControllerUser"
                  icon
                  x-small
                  color="grey"
                  class="mr-1"
                  @click="openEditEventDialog(evt)">
                  <v-icon small>
                    mdi-pencil
                  </v-icon>
                </v-btn>
                <v-btn
                  v-if="isControllerUser"
                  icon
                  x-small
                  color="error"
                  @click="confirmRemoveEvent(evt)">
                  <v-icon small>
                    mdi-delete
                  </v-icon>
                </v-btn>
              </v-card>
            </div>

            <div
              v-if="!loading && !selectedDayCollections.length && !selectedDayEvents.length"
              class="text-center py-4">
              <v-icon
                color="grey lighten-1"
                class="mb-1">
                mdi-calendar-blank
              </v-icon>
              <div class="caption grey--text">
                ไม่มี Collection หรือ Event ในวันนี้
              </div>
            </div>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Add/Edit Event Dialog -->
    <v-dialog
      v-model="showAddEventDialog"
      max-width="400px">
      <v-card rounded="lg">
        <v-toolbar
          color="secondary"
          dark
          flat
          dense>
          <v-icon
            small
            class="mr-2">
            mdi-calendar-plus
          </v-icon>
          <span class="subtitle-2">{{ editingEvent ? 'Edit Event' : 'Create Event' }}</span>
          <v-spacer />
          <v-btn
            icon
            small
            @click="closeEventDialog">
            <v-icon small>
              mdi-close
            </v-icon>
          </v-btn>
        </v-toolbar>

        <v-form
          ref="eventFormRef"
          class="pa-4">
          <v-text-field
            v-model="eventForm.name"
            outlined
            dense
            :rules="[v => !!v || 'กรุณากรอกชื่อ Event']"
            placeholder="Add Title"
            class="mb-3" />
          <v-textarea
            v-model="eventForm.description"
            outlined
            dense
            placeholder="Description (optional)"
            rows="2"
            class="mb-3" />
          <v-menu
            v-model="showRangePicker"
            :close-on-content-click="false"
            offset-y
            min-width="290px">
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                :value="rangeDateLabel"
                outlined
                dense
                :rules="[v => !!v || 'กรุณาเลือกวันที่']"
                placeholder="Select date range"
                readonly
                prepend-inner-icon="mdi-calendar"
                class="mb-3"
                v-bind="attrs"
                v-on="on" />
            </template>
            <v-date-picker
              v-model="eventForm.dates"
              range
              no-title
              color="secondary"
              @input="onRangePicked" />
          </v-menu>
          <v-btn
            block
            color="black"
            dark
            elevation="0"
            @click="saveEvent">
            <v-icon
              left
              small>
              {{ editingEvent ? 'mdi-content-save-outline' : 'mdi-plus' }}
            </v-icon>
            {{ editingEvent ? 'Save Event' : 'Create Event' }}
          </v-btn>
        </v-form>
      </v-card>
    </v-dialog>

    <!-- Delete Confirm Dialog -->
    <v-dialog
      v-model="showDeleteDialog"
      max-width="400px">
      <v-card rounded="lg">
        <v-card-title class=" font-weight-bold">
          Confirm Delete
        </v-card-title>
        <v-card-text>
          Are you sure you want to delete "{{ eventToDelete ? eventToDelete.name : '' }}"?
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="black"
            text
            class="mb-4"
            @click="showDeleteDialog = false">
            Cancel
          </v-btn>
          <v-btn
            color="error"
            text
            class="mb-4"
            @click="removeEvent">
            Confirm
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import ProductCollectionProvider from '@/resources/ProductCollectionProvider'
import ProductionPlanProvider from '@/resources/ProductionPlanProvider'
import MonthCalendar from '@/views/productionPlan/components/MonthCalendar.vue'
import { mapActions } from 'vuex'
import { getRole } from '@/assets/js/Authentication'

const ProductCollectionService = new ProductCollectionProvider()
const ProductionPlanService = new ProductionPlanProvider()

export default {
  name: 'ProductionPlanCalendar',

  components: {
    MonthCalendar
  },

  data () {
    return {
      selectedDay: this.$dayjs().format('YYYY-MM-DD'),
      currentMonth: this.$dayjs().format('MM-YYYY'),

      collections: [],
      events: [],
      loading: false,
      userRole: null,
      showRangePicker: false,
      showAddEventDialog: false,
      showDeleteDialog: false,
      eventToDelete: null,
      editingEvent: null,
      eventForm: {
        name: '',
        description: '',
        dates: [this.$dayjs().format('YYYY-MM-DD'), this.$dayjs().format('YYYY-MM-DD')]
      }
    }
  },

  computed: {
    collectionsByDate () {
      const map = {}
      this.collections.forEach((col) => {
        if (!col.salesDate) return
        const date = this.$dayjs(col.salesDate).format('YYYY-MM-DD')
        if (!map[date]) map[date] = []
        map[date].push(col)
      })
      return map
    },

    eventsByDate () {
      const map = {}
      this.events.forEach((evt) => {
        if (!evt.startDate) return
        let current = this.$dayjs(evt.startDate).startOf('day')
        const end = this.$dayjs(evt.endDate || evt.startDate).startOf('day')
        while (current.isBefore(end, 'day') || current.isSame(end, 'day')) {
          const date = current.format('YYYY-MM-DD')
          if (!map[date]) {
            map[date] = []
          }
          map[date].push(evt)
          current = current.add(1, 'day')
        }
      })
      return map
    },

    rangeDateLabel () {
      if (!this.eventForm.dates.length) return ''
      const sorted = [...this.eventForm.dates].sort()
      if (sorted.length === 1) return sorted[0]
      return `${sorted[0]} - ${sorted[1]}`
    },

    selectedDayCollections () {
      return this.collectionsByDate[this.selectedDay] || []
    },

    selectedDayEvents () {
      return this.eventsByDate[this.selectedDay] || []
    },

    isControllerUser () {
      return ['merchandising_planner', 'developer', 'management'].includes(this.userRole)
    },

    uniqueBrands () {
      const brands = this.collections.map((col) => col.brand).filter(Boolean)
      return [...new Set(brands)].sort()
    }
  },

  watch: {
    selectedDay (val) {
      this.eventForm.dates = [val, val]
    }
  },

  mounted () {
    this.userRole = getRole()
    this.fetchCollections()
    this.fetchEvents()
  },

  methods: {
    ...mapActions({
      setSnackbar: 'Components/setSnackbar'
    }),

    onMonthChange (monthYear) {
      const d = this.$dayjs(`${monthYear}-01`)
      this.currentMonth = d.format('MM-YYYY')
      this.fetchCollections()
      this.fetchEvents()
    },

    async fetchCollections () {
      this.loading = true
      try {
        const { data } = await ProductCollectionService.getCollectionSaleDateCalendar({
          monthYear: this.currentMonth
        })
        this.collections = data
      } catch (e) {
        console.error(e)
        this.setSnackbar({
          value: true,
          message: 'Load Production Plan Calendar Failed',
          type: 'error'
        })
      } finally {
        this.loading = false
      }
    },

    async fetchEvents () {
      try {
        const [month, year] = this.currentMonth.split('-')
        const monthStart = this.$dayjs(`${year}-${month}-01`).startOf('month')
        const monthEnd = this.$dayjs(`${year}-${month}-01`).endOf('month')
        const { data } = await ProductionPlanService.getCalendarEvents({
          startDate: monthStart.toISOString(),
          endDate: monthEnd.toISOString()
        })
        this.events = data || []
      } catch (e) {
        console.error(e)
        this.setSnackbar({
          value: true,
          message: 'โหลด Event ไม่สำเร็จ',
          type: 'error'
        })
      }
    },

    onRangePicked () {
      if (this.eventForm.dates.length === 2) {
        this.showRangePicker = false
      }
    },

    closeEventDialog () {
      this.editingEvent = null
      this.showAddEventDialog = false
    },

    openAddEventDialog () {
      const today = this.$dayjs().format('YYYY-MM-DD')
      this.editingEvent = null
      this.eventForm = {
        name: '',
        description: '',
        dates: [today, today]
      }
      this.showAddEventDialog = true
    },

    openEditEventDialog (evt) {
      this.editingEvent = evt
      this.eventForm = {
        name: evt.name,
        description: evt.description || '',
        dates: [
          this.$dayjs(evt.startDate).format('YYYY-MM-DD'),
          this.$dayjs(evt.endDate || evt.startDate).format('YYYY-MM-DD')
        ]
      }
      this.showAddEventDialog = true
    },

    async saveEvent () {
      if (!this.$refs.eventFormRef.validate()) return
      const sorted = [...this.eventForm.dates].sort()
      const startDate = this.$dayjs(sorted[0], 'YYYY-MM-DD')
      const endDate = this.$dayjs(sorted[1], 'YYYY-MM-DD')
      if (endDate.isBefore(startDate, 'day')) {
        this.setSnackbar({
          value: true,
          message: 'วันสิ้นสุดต้องไม่น้อยกว่าวันเริ่ม',
          type: 'error'
        })
        return
      }
      const payload = {
        name: this.eventForm.name,
        description: this.eventForm.description || undefined,
        startDate: startDate.toDate(),
        endDate: endDate.toDate()
      }
      try {
        if (this.editingEvent) {
          await ProductionPlanService.updateCalendarEvent(this.editingEvent.id, payload)
        } else {
          await ProductionPlanService.createCalendarEvent(payload)
        }
        this.editingEvent = null
        this.eventForm.name = ''
        this.eventForm.description = ''
        this.eventForm.dates = this.selectedDay ? [this.selectedDay, this.selectedDay] : []
        this.showAddEventDialog = false
        this.fetchEvents()
      } catch (e) {
        console.error(e)
        this.setSnackbar({
          value: true,
          message: this.editingEvent ? 'แก้ไข Event ไม่สำเร็จ' : 'เพิ่ม Event ไม่สำเร็จ',
          type: 'error'
        })
      }
    },

    confirmRemoveEvent (evt) {
      this.eventToDelete = evt
      this.showDeleteDialog = true
    },

    async removeEvent () {
      try {
        await ProductionPlanService.deleteCalendarEvent(this.eventToDelete.id)
        this.events = this.events.filter((e) => e.id !== this.eventToDelete.id)
      } catch (e) {
        console.error(e)
        this.setSnackbar({
          value: true,
          message: 'ลบ Event ไม่สำเร็จ',
          type: 'error'
        })
      } finally {
        this.showDeleteDialog = false
        this.eventToDelete = null
      }
    },

    getCollectionsForDate (date) {
      return this.collectionsByDate[date] || []
    },

    getEventsForDate (date) {
      return this.eventsByDate[date] || []
    },

    getAssigneesByJob (col) {
      const assignees = col.assignees || []
      return ['designer', 'merchandiser']
        .map((job) => ({
          job,
          label: job.charAt(0).toUpperCase() + job.slice(1),
          names: assignees.filter((a) => a.job === job).map((a) => a.name)
        }))
        .filter((group) => group.names.length)
    },

    getDayItems (date) {
      const cols = this.getCollectionsForDate(date).map((c) => ({ ...c, type: 'collection' }))
      const evts = this.getEventsForDate(date).map((e) => ({ ...e, type: 'event' }))
      return [...cols, ...evts]
    },

    getBrandColor (brand) {
      if (brand === 'Gentlewoman' || brand === 'Gentlewoman Universe') {
        return { bg: 'blue lighten-4', text: 'blue darken-2', border: '#42a5f5' }
      }
      if (brand === 'Gentlelittlewoman') {
        return { bg: '#FFF3E0', text: '#EF6C00', border: '#EF6C00' }
      }
      if (brand === 'Matter Makers') {
        return { bg: 'blue lighten-4', text: 'blue darken-2', border: '#42a5f5' }
      }
      return { bg: '#F5F5F5', text: '#616161', border: '#9E9E9E' }
    }
  }
}
</script>
